/**
 * authService - 登入相關的 URL 參數解析工具
 *
 * 職責（HTTP 呼叫已全部搬到 src/api/*，這裡只留純 URL 解析 + 共用型別）：
 * - 從 URL 解析 dev acc 登入參數 / ticket
 * - 判斷是否走 dev acc 登入
 *
 * token 仍只存於 Pinia 內存（authStore），不落 storage。
 * 實際登入 / 登出 / 余額 / 幣種等 API 見：
 *   src/api/users（fetchToken / loginWithTicket / logout）
 *   src/api/wallets（fetchBalance）
 *   src/api/game（fetchBets）
 */

import { ENABLED_DEV_LOGIN, IS_GUEST_LOGIN } from './runtimeConfig'
import { readGameId } from './composables/useGameId'

/**
 * 開發期帳號登入觸發條件（純 URL 判斷）
 * 前端旗標 `ENABLED_DEV_LOGIN`（runtimeConfig）現恆 true、全環境放行；能不能真的登入由
 * **後端** `POST /v1/users/dev/login` 端點各環境自己 gate（線上不開端點 = 換不到 token）。
 *
 * URL 命中條件：URL **帶有 `acc` 參數**（`has`，不看值）——
 * `?acc=` 空值也算命中、也會打 dev/login；空值時 parseLoginParams 會
 * 自動生成 `test_xxxxxxxx` 隨機帳號補上（session 內固定）。
 */
export function shouldUseDevAccLogin(): boolean {
  return new URL(window.location.href).searchParams.has('acc')
}

/**
 * 登入模式（4 種登入的**單一決策點**；replay 是 view-mode 不在此）。優先序：
 *   1. `'guest'`  — `IS_GUEST_LOGIN`（`config.json.env === 'demo'`，部署層級，最優先）
 *      🛑 **demo 環境只能走 guest/login**：此分支先 return，devAcc / ticket 在 demo
 *      一律不可達（就算 URL 帶 acc 或 ticket 也一樣）。這是刻意的部署層級鎖定，
 *      不要把優先序往下搬。
 *   2. `'ticket'`（URL）— URL 帶 ticket（正式）。**同時帶 acc 與 ticket 時 ticket 優先**
 *      （2026-07-30 定案）：正式憑證永遠壓過開發後門。登入成功後 ticket 與 acc
 *      會**一起**從 URL 清掉（見 stashTicketAndCleanUrl）—— 只清 ticket 的話，
 *      複製殘留 `?acc=` 的網址去新分頁會靜默降級成 devAcc 換人。
 *   3. `'devAcc'` — URL **帶有** `acc` 參數（空值也算命中、也會打 dev/login；
 *      全環境放行，帳號有沒有效由後端端點判）
 *   4. `'ticket'`（storage）— URL 什麼憑證都沒帶，但 sessionStorage 有同一
 *      gameId 的 stash ticket（= ticket 登入後 F5 / 續期的路）。
 *      🛑 **storage 殘留必須排在 URL 憑證之後**：排前面的話，同一個 tab 用過
 *      ticket 之後再貼 `?acc=` 的 devAcc 網址，會被舊 ticket 劫持、靜默換人。
 *   5. `null`     — 無法登入（缺 ticket）→ caller 顯示「缺少 ticket」
 *
 * auth store 的 login()/refreshAuth() 依此分派；集中在此避免各處 if 散落。
 */
export type LoginMode = 'guest' | 'devAcc' | 'ticket'

export function resolveLoginMode(): LoginMode | null {
  if (IS_GUEST_LOGIN) return 'guest'
  if (parseUrlTicket()) return 'ticket' // URL 憑證優先序：ticket > acc（見上）
  if (ENABLED_DEV_LOGIN && shouldUseDevAccLogin()) return 'devAcc'
  if (parseStoredTicket()) return 'ticket' // storage 殘留殿後（F5 / 續期的路）
  return null
}

// 不作為登入參數的保留欄位。
// `ticket` 也要擋：它是 ticket 模式的憑證，devAcc 模式下若 URL 帶著它，
// 不擋會被當一般參數塞進 dev login body 外洩給另一個端點。
// `dokomoii`：已廢棄的舊 devAcc 開關（2026-07-30 移除），不再有任何作用 ——
// 但舊書籤 / 平台殘留的 URL 還會帶著它，擋掉免得混進 dev login body。
const RESERVED_PARAMS = new Set(['gameId', 'token', 'ticket', 'dokomoii'])

/**
 * 生成隨機測試帳號（格式：test_xxxxxxxx）
 */
function generateRandomAccount(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let suffix = ''
  for (let i = 0; i < 8; i++) {
    suffix += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return `test_${suffix}`
}

/**
 * acc 空值時自動生成的隨機帳號 —— **模組級快取，一個 session 只擲一次骰**。
 *
 * 🛑 沒有這個快取時，`refreshAuth()`（401 續期）重呼叫 parseLoginParams() 會拿到
 * **另一個**隨機帳號 → 續期悄悄換成新測試玩家（同 guest 換人問題）。
 * F5 重新整理才拿新帳號（模組重載、快取歸零）—— 刻意的，開發者靠它快速換號。
 */
let generatedAcc: string | null = null

/**
 * 從 URL 解析登入參數（排除保留欄位）。
 *
 * `acc` 規則（2026-07-30 定案）：
 *   - **URL 沒帶 acc 參數** → 原樣返回，什麼都不補（此時也不會是 devAcc 模式）。
 *   - **帶了但空值**（`?acc=`）→ 自動生成 `test_xxxxxxxx`（session 內固定，見 generatedAcc）。
 *   - 帶了有值 → 用 URL 的。
 */
export function parseLoginParams(): Record<string, string> {
  const url = new URL(window.location.href)
  const params: Record<string, string> = {}

  url.searchParams.forEach((value, key) => {
    if (!RESERVED_PARAMS.has(key)) {
      params[key] = value
    }
  })

  if ('acc' in params && params['acc'].trim() === '') {
    generatedAcc ??= generateRandomAccount()
    params['acc'] = generatedAcc
  }

  return params
}

/**
 * ticket 的 sessionStorage key —— **綁 gameId**。
 * 同一個 tab 先進遊戲 A（ticket 已 stash）、再載入遊戲 B 的網址時，
 * A 的殘留 ticket 對 B **不可見**（不會拿舊 ticket + 新 gameCode 去撞出
 * ticketInvalid），只有回到同一款才會被拿來續登。
 */
const ticketStorageKey = (): string =>
  `host:ticket:${readGameId(new URL(window.location.href)) ?? ''}`

/** URL 上的 ticket（明確憑證） */
function parseUrlTicket(): string | null {
  return new URL(window.location.href).searchParams.get('ticket')?.trim() || null
}

/** sessionStorage 的 stash ticket（同 gameId 才可見；per-tab、關 tab 即失效） */
function parseStoredTicket(): string | null {
  try {
    return sessionStorage.getItem(ticketStorageKey()) || null
  } catch {
    return null
  }
}

/**
 * 取 ticket：URL 優先、storage 殿後（登入 / 續期共用）。
 * ⚠️ **模式判定不要用這支** —— resolveLoginMode 需要區分兩個來源
 * （URL 憑證 > acc > storage 殘留），用 parseUrlTicket / parseStoredTicket。
 */
export function parseTicket(): string | null {
  return parseUrlTicket() ?? parseStoredTicket()
}

/**
 * 登入成功後呼叫：把 URL 上的 ticket 收進 sessionStorage，並用 replaceState
 * 從網址列清掉 —— ticket 是可重複換 token 的憑證（後端 2026-07 改版後），
 * 留在網址列會跟著截圖 / 複製連結 / browser history 外流。
 *
 * 🛑 **`acc` 一併清**：ticket 存在時 acc 是被優先序壓過的殘留參數，但 ticket
 * 清掉後它就「浮上來」了 —— 複製這條網址去新分頁（sessionStorage 不跟過去）
 * 會落到 devAcc、**默默用測試帳號登入換人**（`?acc=` 空值還會生成隨機號）。
 * 一起清掉，複製出去的網址就乾淨地走「缺少 ticket」錯誤頁，不會靜默降級。
 *
 * - sessionStorage 不可用（隱私模式等）→ **什麼都不清**，維持可重新登入優先。
 * - URL 本來就沒 ticket（F5 後 / guest / devAcc 模式）→ no-op（devAcc 的 acc 不受影響）。
 * - 同 tab F5 / 401 續期走 parseTicket() 的 sessionStorage fallback，仍是 ticket 模式。
 */
export function stashTicketAndCleanUrl(): void {
  const url = new URL(window.location.href)
  const ticket = url.searchParams.get('ticket')?.trim()
  if (!ticket) return
  try {
    sessionStorage.setItem(ticketStorageKey(), ticket)
  } catch {
    return
  }
  url.searchParams.delete('ticket')
  url.searchParams.delete('acc')
  history.replaceState(history.state, '', url.toString())
}

/**
 * 用戶資訊（傳遞給子頁面）
 */
export interface UserInfo {
  userId: string
  balance: number
}
